import React, { createRef, RefObject } from 'react';
import { scroller } from 'react-scroll';
import Link from 'next/link';
import GalleryFilter, { FilterOptions } from 'partials/gallery/GalleryFilter';
import { GalleryItemWithBundle } from 'types';
import DoubleHeader from 'components/typography/DoubleHeader';
import NextSteps from 'components/NextSteps';
import SalesCalloutGroomsFree from 'components/SalesCalloutGroomsFree';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import tw from '../../tailwind.config';
import { getGalleryItems } from 'services/gallery';
import { withRouter } from 'next/router';
import { WithRouterProps } from 'next/dist/client/with-router';
import { withPageStaticProps } from 'utils/header';
import { capitalizeFirstLetter } from '@root/src/utils/utils';

interface Props {
  items: GalleryItemWithBundle[];
}

interface State {
  items: GalleryItem[];
}

type GalleryItem = GalleryItemWithBundle & {
  matchesFilterOptions: boolean;
};

class Gallery extends React.Component<Props & WithRouterProps, State> {
  protected topOfGallery: RefObject<HTMLDivElement>;

  constructor(props: Props & WithRouterProps) {
    super(props);

    this.state = {
      items: props.items.map((item) => ({
        ...item,
        matchesFilterOptions: true,
      })),
    };

    this.topOfGallery = createRef<HTMLDivElement>();

    this.doesImageMatchFilters = this.doesImageMatchFilters.bind(this);
    this.getItemsMatchingActiveFilters = this.getItemsMatchingActiveFilters.bind(this);
    this.toggleFilteredItems = this.toggleFilteredItems.bind(this);
    this.scrollToTopRealNice = this.scrollToTopRealNice.bind(this);
  }

  scrollToTopRealNice() {
    const anchorOffset = this.topOfGallery.current?.getBoundingClientRect()?.top ?? 0;

    if (window.scrollY > anchorOffset) {
      scroller.scrollTo('scroll-to-here', {
        duration: 300,
        smooth: 'easeInOutCubic',
        offset: -32,
      });
    }
  }

  getItemsMatchingActiveFilters() {
    return this.state.items
      .sort((a, b) => `${a.orderRank}`.localeCompare(`${b.orderRank}`))
      .filter((image) => image.matchesFilterOptions);
  }

  toggleFilteredItems(options: FilterOptions) {
    this.setState(
      (state) => ({
        items: state.items.map((item) => ({
          ...item,
          matchesFilterOptions: this.doesImageMatchFilters(item, options),
        })),
      }),
      () => this.scrollToTopRealNice()
    );
  }

  /**
   * Returns a boolean indicating whether or not the given `image` should be
   * displayed in the list, based on the currently active filters
   */
  doesImageMatchFilters(image: GalleryItem, options: FilterOptions) {
    const matchesColor = options.color.length === 0 || options.color.includes(capitalizeFirstLetter(image.color ?? ''));

    const matchesStyle = options.style.length === 0 || options.style.includes(image.type ?? '');

    const matchesCategory = options.category.length === 0 || options.category.includes(image.category ?? '');

    return matchesColor && matchesStyle && matchesCategory;
  }

  render() {
    const activeItems = this.getItemsMatchingActiveFilters();

    return (
      <>
        <div className="bg-gray-lighter py-1">
          <div className="m-section-hero">
            <div className="container">
              <div className="row">
                <div className="col-span-12">
                  <h1>
                    <DoubleHeader hero smallTitle="Inspiration" largeTitle="Wedding Gallery" />
                  </h1>
                  <p className="mt-32">
                    Get inspired by real Generation Tux customers. Explore a range of wedding themes, see examples of
                    our suit and tuxedo styles, unlock endless color combinations, and more. When you see a suit or
                    tuxedo you like, simply click on the style to design your look.
                  </p>
                </div>
              </div>

              <div className="row mt-32 items-stretch sm:mt-64" id="scroll-to-here" ref={this.topOfGallery}>
                <div className="col-span-12 mb-32 sm:col-span-4 md:col-span-3">
                  <div className="sticky top-32">
                    <GalleryFilter
                      colors={['Blue', 'Black', 'Tan', 'Gray', 'White']}
                      styles={['suit', 'tux']}
                      categories={['Styled Shoot', 'Real Wedding']}
                      onToggle={(options: FilterOptions) => this.toggleFilteredItems(options)}
                    />

                    <Link href="/collection/[category]" as="/collection/tuxedos-and-suits">
                      <a className="tracker-cta-gallery-sidebar-20190110-074947 btn-info btn mt-32 block w-full px-0 text-center">
                        View Collection
                      </a>
                    </Link>
                  </div>
                </div>

                <div className="col-span-12 sm:col-span-8 md:col-span-9">
                  <div className="grid w-full grid-cols-2 gap-x-16 gap-y-32 md:grid-cols-3 lg:grid-cols-4">
                    {activeItems.map((item: GalleryItemWithBundle) => (
                      <div
                        key={item._id}
                        id={`gallery-item-${item._id}`}
                        className="block cursor-pointer"
                        onClick={() => this.props.router.push(`/gallery/post/${item._id}`)}
                      >
                        <div className="aspect-ratio-1by1">
                          <picture>
                            <source
                              media={`(min-width: ${tw.theme.screens['lg']})`}
                              srcSet={`${item.image?.asset.url}?fit=crop&w=276&h=276`}
                            />
                            <source
                              media={`(min-width: ${tw.theme.screens['sm']})`}
                              srcSet={`${item.image?.asset.url}?fit=crop&w=208&h=208`}
                            />
                            <img
                              className="transition-150 w-full filter transition hover:shadow-3 hover:brightness-[108%]"
                              src={`${item.image?.asset.url}?fit=crop&w=382&h=382`}
                              alt={`${item.bundle.display_name} ${
                                item.photoCredit !== '' ? `- Image by ${item.photoCredit}` : ''
                              }`}
                              loading="lazy"
                            />
                          </picture>
                        </div>

                        <p className="white mt-8 overflow-hidden overflow-ellipsis whitespace-nowrap text-sm text-gray-dark">
                          {item.bundle.display_name ? item.bundle.display_name : `\u00a0`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="sm:128 mt-64 bg-white">
            <NextSteps
              ctaPageName="gallery"
              salesCallout1={<SalesCalloutGroomsFree ctaPageName="gallery" />}
              salesCallout2={<SalesCalloutSwatches ctaPageName="gallery" />}
            />
          </div>
        </div>
      </>
    );
  }
}

export default withRouter(Gallery);

export const getStaticProps = withPageStaticProps(async () => {
  try {
    const items = await getGalleryItems();

    return {
      props: {
        items,
      },
    };
  } catch (error) {
    console.error('Failed to create gallery', { error });
    throw error;
  }
});
